
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import PropertyCard from '@/components/PropertyCard';
import NavigationHeader from '@/components/NavigationHeader';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Filter, MapPin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';

interface Property {
  id: string;
  title: string;
  description: string;
  address: string;
  monthly_price: number;
  type: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  floor_number?: number;
  building_age?: number;
  furnishing_status: string;
  features: string[];
  available_from: string;
  occupied: boolean;
  views: number;
  is_active: boolean;
  average_rating?: number;
  total_ratings?: number;
}

const Properties = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState('all');
  const [propertyType, setPropertyType] = useState('all');
  const [furnishingStatus, setFurnishingStatus] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const { data: allProperties = [], isLoading, error, refetch } = useQuery({
    queryKey: ['properties', searchTerm, priceRange, propertyType, furnishingStatus, sortBy],
    queryFn: async () => {
      console.log('جلب العقارات النشطة...');
      
      let query = supabase
        .from('properties')
        .select('*')
        .eq('is_active', true);

      // البحث بالعنوان أو العنوان
      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,address.ilike.%${searchTerm}%`);
      }

      // فلتر نوع العقار
      if (propertyType && propertyType !== 'all') {
        query = query.eq('type', propertyType);
      }

      // فلتر حالة الأثاث
      if (furnishingStatus && furnishingStatus !== 'all') {
        query = query.eq('furnishing_status', furnishingStatus);
      }

      // فلتر السعر
      if (priceRange && priceRange !== 'all') {
        const [min, max] = priceRange.split('-').map(Number);
        if (max) {
          query = query.gte('monthly_price', min).lte('monthly_price', max);
        } else {
          query = query.gte('monthly_price', min);
        }
      }

      // الترتيب
      switch (sortBy) {
        case 'price_low':
          query = query.order('monthly_price', { ascending: true });
          break;
        case 'price_high':
          query = query.order('monthly_price', { ascending: false });
          break;
        case 'newest':
          query = query.order('created_at', { ascending: false });
          break;
        case 'popular':
          query = query.order('views', { ascending: false });
          break;
        case 'highest_rated':
          query = query.order('average_rating', { ascending: false });
          break;
        default:
          query = query.order('created_at', { ascending: false });
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // تحويل features من Json إلى string[]
      return data?.map(property => ({
        ...property,
        features: Array.isArray(property.features) 
          ? property.features.filter((f): f is string => typeof f === 'string')
          : [],
        views: property.views || 0,
        average_rating: property.average_rating || 0,
        total_ratings: property.total_ratings || 0
      })) || [];
    },
  });

  // حساب بيانات الصفحة الحالية
  const totalPages = Math.ceil(allProperties.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const properties = allProperties.slice(startIndex, endIndex);

  // إعادة تعيين الصفحة الحالية عند تغيير الفلاتر
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, priceRange, propertyType, furnishingStatus, sortBy]);

  const incrementViews = async (propertyId: string) => {
    try {
      const { error } = await supabase.rpc('increment_property_views', { 
        property_uuid: propertyId 
      });
      
      if (!error) {
        refetch();
      }
    } catch (error) {
      // Silent error handling
    }
  };

  // تحديث المشاهدات عند الضغط على بطاقة العقار
  const handlePropertyView = (propertyId: string) => {
    incrementViews(propertyId);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <NavigationHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل العقارات...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <NavigationHeader />
        <div className="flex-1 flex items-center justify-center">
          <Card className="p-8 text-center">
            <CardContent>
              <p className="text-red-600 mb-4">حدث خطأ في تحميل العقارات</p>
              <Button onClick={() => refetch()} variant="outline">
                إعادة المحاولة
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <NavigationHeader />
      
      {/* شريط البحث والفلاتر */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col gap-4">
            {/* صف البحث */}
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="البحث عن عقار..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 text-right"
                />
              </div>
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={() => {
                  setSearchTerm('');
                  setPriceRange('all');
                  setPropertyType('all');
                  setFurnishingStatus('all');
                  setSortBy('newest');
                }}
              >
                <Filter className="h-4 w-4" />
                مسح الفلاتر
              </Button>
            </div>

            {/* صف الفلاتر */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Select value={propertyType} onValueChange={setPropertyType}>
                <SelectTrigger>
                  <SelectValue placeholder="نوع العقار" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الأنواع</SelectItem>
                  <SelectItem value="شقة">شقة</SelectItem>
                  <SelectItem value="فيلا">فيلا</SelectItem>
                  <SelectItem value="مكتب">مكتب</SelectItem>
                  <SelectItem value="محل">محل تجاري</SelectItem>
                </SelectContent>
              </Select>

              <Select value={priceRange} onValueChange={setPriceRange}>
                <SelectTrigger>
                  <SelectValue placeholder="النطاق السعري" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الأسعار</SelectItem>
                  <SelectItem value="0-300">أقل من 300 ر.ع</SelectItem>
                  <SelectItem value="300-500">300 - 500 ر.ع</SelectItem>
                  <SelectItem value="500-800">500 - 800 ر.ع</SelectItem>
                  <SelectItem value="800-1200">800 - 1200 ر.ع</SelectItem>
                  <SelectItem value="1200">أكثر من 1200 ر.ع</SelectItem>
                </SelectContent>
              </Select>

              <Select value={furnishingStatus} onValueChange={setFurnishingStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="حالة الأثاث" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الحالات</SelectItem>
                  <SelectItem value="furnished">مفروش</SelectItem>
                  <SelectItem value="semi_furnished">مفروش جزئياً</SelectItem>
                  <SelectItem value="unfurnished">غير مفروش</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue placeholder="ترتيب حسب" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">الأحدث</SelectItem>
                  <SelectItem value="popular">الأكثر مشاهدة</SelectItem>
                  <SelectItem value="highest_rated">الأكثر تقييماً</SelectItem>
                  <SelectItem value="price_low">السعر: من الأقل</SelectItem>
                  <SelectItem value="price_high">السعر: من الأعلى</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">
                  {allProperties.length} عقار متاح
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* قائمة العقارات */}
      <div className="flex-1 container mx-auto px-4 py-8">
        {allProperties.length === 0 ? (
          <Card className="p-12 text-center">
            <CardContent>
              <div className="text-gray-400 mb-4">
                <MapPin className="h-16 w-16 mx-auto mb-4 opacity-50" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                لا توجد عقارات متاحة
              </h3>
              <p className="text-gray-500 mb-4">
                لم يتم العثور على عقارات تطابق معايير البحث المحددة
              </p>
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchTerm('');
                  setPriceRange('all');
                  setPropertyType('all');
                  setFurnishingStatus('all');
                  setSortBy('newest');
                }}
              >
                مسح جميع الفلاتر
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* إحصائيات البحث */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-sm">
                  {allProperties.length} عقار متاح
                </Badge>
                {searchTerm && (
                  <Badge variant="outline" className="text-sm">
                    البحث: "{searchTerm}"
                  </Badge>
                )}
              </div>
            </div>

            {/* شبكة العقارات */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {properties.map((property) => (
                <div key={property.id} onClick={() => handlePropertyView(property.id)}>
                  <PropertyCard property={property} />
                </div>
              ))}
            </div>

            {/* التنقل بين الصفحات */}
            {totalPages > 1 && (
              <div className="mt-12 flex justify-center">
                <Pagination>
                  <PaginationContent>
                    {currentPage > 1 && (
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => setCurrentPage(currentPage - 1)}
                          className="cursor-pointer"
                        />
                      </PaginationItem>
                    )}
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNumber;
                      if (totalPages <= 5) {
                        pageNumber = i + 1;
                      } else if (currentPage <= 3) {
                        pageNumber = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNumber = totalPages - 4 + i;
                      } else {
                        pageNumber = currentPage - 2 + i;
                      }
                      
                      return (
                        <PaginationItem key={pageNumber}>
                          <PaginationLink
                            onClick={() => setCurrentPage(pageNumber)}
                            isActive={currentPage === pageNumber}
                            className="cursor-pointer"
                          >
                            {pageNumber}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}
                    
                    {totalPages > 5 && currentPage < totalPages - 2 && (
                      <>
                        <PaginationItem>
                          <PaginationEllipsis />
                        </PaginationItem>
                        <PaginationItem>
                          <PaginationLink
                            onClick={() => setCurrentPage(totalPages)}
                            className="cursor-pointer"
                          >
                            {totalPages}
                          </PaginationLink>
                        </PaginationItem>
                      </>
                    )}
                    
                    {currentPage < totalPages && (
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => setCurrentPage(currentPage + 1)}
                          className="cursor-pointer"
                        />
                      </PaginationItem>
                    )}
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Properties;
